import { SOCKET_CONFIG } from '../config/constants';
import { getRuntimeConfig } from '../config/runtime';
import { eventCentral } from './eventCentral';

// ── Types ───────────────────────────────────────────────────────────────────

/**
 * Granular connection states so the UI can show exactly what's happening.
 */
export type ConnectionState =
  | 'disconnected'    // No socket, not trying
  | 'connecting'      // Socket created, waiting for onopen
  | 'connected'       // onopen fired, not yet authenticated
  | 'authenticating'  // Auth request in flight
  | 'authenticated'   // Fully ready
  | 'reconnecting'    // Scheduled reconnect (backoff timer running)
  | 'failed';         // All reconnect attempts exhausted

export interface ConnectionHealth {
  state: ConnectionState;
  /** Current round-trip latency in ms (from heartbeat) */
  latencyMs: number | null;
  /** Which reconnect attempt we're on (0 = not reconnecting) */
  reconnectAttempt: number;
  /** Max reconnect attempts before giving up */
  maxReconnectAttempts: number;
  /** Estimated seconds until next reconnect attempt (null if not reconnecting) */
  reconnectInSeconds: number | null;
  /** Whether the browser reports navigator.onLine */
  isNetworkOnline: boolean;
  /** Timestamp of last successful pong */
  lastPingReceivedAt: number | null;
  /** Timestamp of when the socket first connected in this session */
  connectedSince: number | null;
  /**
   * True once latency has read slow on SLOW_LATENCY_CONSECUTIVE_SAMPLES
   * probes in a row. Debounced so one noisy sample doesn't flip the UI —
   * check this rather than comparing latencyMs to a threshold directly.
   */
  isLatencyElevated: boolean;
}

// Configuration options from environment variables
interface SocketConfig {
  reconnectionDelay: number;
  reconnectionDelayMax: number;
  reconnectionAttempts: number;
  randomizationFactor: number;
}

const getSocketConfig = (): SocketConfig => ({
  reconnectionDelay: SOCKET_CONFIG.RECONNECTION_DELAY,
  reconnectionDelayMax: SOCKET_CONFIG.RECONNECTION_DELAY_MAX,
  reconnectionAttempts: SOCKET_CONFIG.RECONNECTION_ATTEMPTS,
  randomizationFactor: SOCKET_CONFIG.RANDOMIZATION_FACTOR,
});

interface SocketStatusCallbacks {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onReconnecting?: (attemptNumber: number) => void;
  onError?: (error: Error) => void;
  onNetworkOffline?: () => void;
  onNetworkOnline?: () => void;
  onLogout?: () => void;
}

// ── Heartbeat config ────────────────────────────────────────────────────────

/**
 * How often the portal probes the connection with an ApplicationStatusRequest.
 *
 * The portal drives this rather than waiting to be pinged. c1-services has no
 * server-initiated heartbeat — its scheduler processes billable events and
 * nothing else — so a watchdog that arms only on an inbound ping never armed at
 * all. It does answer a client ApplicationStatusRequest with an
 * ApplicationStatus, so the exchange works today with no backend change.
 */
const HEARTBEAT_PROBE_INTERVAL = 30_000;

/**
 * Treat the connection as dead after this long with no inbound traffic.
 *
 * Three probe intervals, so two replies can be lost before we act. Any inbound
 * message resets it, not just a probe reply: real traffic proves liveness just
 * as well, and that avoids tearing down a busy connection whose reply happened
 * to be delayed.
 */
const HEARTBEAT_STALE_TIMEOUT = 90_000;

/**
 * Latency above this many ms is "slow." A single reading above this alone
 * isn't enough to tell the user — see SLOW_LATENCY_CONSECUTIVE_SAMPLES.
 */
export const SLOW_LATENCY_THRESHOLD_MS = 500;

/**
 * Require latency to read slow on this many consecutive probe replies
 * before surfacing it as "Slow connection" in the UI. One noisy sample
 * amid normal mobile 3G/4G jitter is common and not worth alarming the
 * user over — the market UX baseline is to debounce a transient state
 * before showing it, not just before hiding it. Two in a row (one full
 * HEARTBEAT_PROBE_INTERVAL apart) is a much stronger signal.
 */
export const SLOW_LATENCY_CONSECUTIVE_SAMPLES = 2;

// ── Service ─────────────────────────────────────────────────────────────────

class SocketService {
  private socket: WebSocket | null = null;
  private config: SocketConfig;
  private maxReconnectAttempts: number;
  private reconnectAttempt = 0;
  private everConnected = false;
  private isManuallyDisconnected = false;
  private statusCallbacks: SocketStatusCallbacks = {};
  private reconnectTimeout: any = null;
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private messageQueue: any[] = [];
  private isReconnecting = false;

  // ── Heartbeat state (portal probes → tracks replies) ─────────────────
  private heartbeatStaleTimer: any = null;
  private heartbeatProbeTimer: any = null;
  private _latencyMs: number | null = null;
  private _lastPingReceivedAt: number | null = null;
  private _consecutiveSlowSamples = 0;

  // ── Connection health state ──────────────────────────────────────────
  private _state: ConnectionState = 'disconnected';
  private _connectedSince: number | null = null;
  private _reconnectInSeconds: number | null = null;
  private _reconnectCountdownTimer: any = null;
  private healthListeners: Set<(health: ConnectionHealth) => void> = new Set();

  // ── Network listener guard ───────────────────────────────────────────
  private networkListenersAttached = false;
  private boundOnOnline: (() => void) | null = null;
  private boundOnOffline: (() => void) | null = null;

  // ── Visibility listener guard ────────────────────────────────────────
  private visibilityListenerAttached = false;
  private boundOnVisibility: (() => void) | null = null;

  // ── navigator.onLine poll (catches DevTools offline which skips events) ─
  private onlinePollTimer: any = null;

  constructor() {
    this.config = getSocketConfig();
    this.maxReconnectAttempts = this.config.reconnectionAttempts;
  }

  // ── Connection health API ────────────────────────────────────────────

  /** Get a snapshot of current connection health. */
  getHealth(): ConnectionHealth {
    return {
      state: this._state,
      latencyMs: this._latencyMs,
      reconnectAttempt: this.reconnectAttempt,
      maxReconnectAttempts: this.maxReconnectAttempts,
      reconnectInSeconds: this._reconnectInSeconds,
      isNetworkOnline: this.isNetworkOnline(),
      lastPingReceivedAt: this._lastPingReceivedAt,
      connectedSince: this._connectedSince,
      isLatencyElevated: this._consecutiveSlowSamples >= SLOW_LATENCY_CONSECUTIVE_SAMPLES,
    };
  }

  /** Subscribe to health changes. Returns unsubscribe function. */
  onHealthChange(listener: (health: ConnectionHealth) => void): () => void {
    this.healthListeners.add(listener);
    // Immediately emit current state
    listener(this.getHealth());
    return () => { this.healthListeners.delete(listener); };
  }

  private setState(state: ConnectionState): void {
    this._state = state;
    this.notifyHealthListeners();
  }

  private notifyHealthListeners(): void {
    const health = this.getHealth();
    this.healthListeners.forEach((fn) => fn(health));
  }

  // ── Connect ──────────────────────────────────────────────────────────

  connect(callbacks?: SocketStatusCallbacks): WebSocket {
    if (callbacks) {
      this.statusCallbacks = { ...this.statusCallbacks, ...callbacks };
    }

    // React StrictMode and overlapping consumers can request a connection while
    // the first handshake is still in flight. Reuse that socket rather than
    // closing a CONNECTING socket, which produces the browser warning reported
    // in AL-367 and starts a second unnecessary handshake.
    if (
      this.socket?.readyState === WebSocket.OPEN ||
      this.socket?.readyState === WebSocket.CONNECTING
    ) {
      return this.socket;
    }

    this.isManuallyDisconnected = false;
    this.setState('connecting');

    // Get WebSocket URL from runtime config
    const runtimeConfig = getRuntimeConfig();
    let socketUrl = runtimeConfig?.VITE_AWS_SOCKET || SOCKET_CONFIG.URL;

    if (socketUrl?.endsWith('/')) {
      socketUrl = socketUrl.slice(0, -1);
    }

    // Close any existing socket before creating a new one
    this.closeExistingSocket();

    this.socket = new WebSocket(socketUrl);

    this.setupEventListeners();
    this.setupNetworkListeners();
    this.setupVisibilityListener();

    return this.socket;
  }

  private closeExistingSocket(): void {
    if (!this.socket) return;
    try {
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close();
      }
    } catch (_) {
      // ignore
    }
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    // Pin every handler to this specific instance. Call sites that close a
    // socket directly (boundOnOffline, handleStaleConnection,
    // boundOnVisibility) don't strip its handlers first the way
    // closeExistingSocket() does — a real close() is asynchronous, so if a
    // newer socket replaces this.socket before this one's close finishes,
    // its onclose/onopen still fire afterward. Without this guard they'd
    // run against the CURRENT singleton state (this.attemptReconnection(),
    // etc.) even though this socket is no longer the live one — exactly
    // what happens under rapid connectivity flapping (a real mobile
    // scenario), producing extra reconnect cycles and extra sockets.
    const socket = this.socket;
    const isCurrent = () => this.socket === socket;

    socket.onopen = () => {
      if (!isCurrent()) return;

      this.everConnected = true;
      this.isReconnecting = false;
      this._connectedSince = Date.now();
      this._reconnectInSeconds = null;
      this.clearReconnectCountdown();

      this.setState('connected');
      this.startHeartbeat();

      this.statusCallbacks.onConnected?.();
    };

    socket.onclose = (_event) => {
      if (!isCurrent()) return;

      this.stopHeartbeat();
      this._connectedSince = null;

      // Drop any partially-received segmented replies (AL-311) so a dropped
      // final segment does not leak its accumulator across reconnects.
      eventCentral.clearSegmentBuffers();

      this.statusCallbacks.onDisconnected?.();

      if (!this.isManuallyDisconnected) {
        this.setState('reconnecting');
        this.attemptReconnection();
      } else {
        this.setState('disconnected');
      }
    };

    socket.onerror = (_error) => {
      if (!isCurrent()) return;
      this.statusCallbacks.onError?.(new Error('WebSocket connection failed - check URL and server status'));
    };

    socket.onmessage = (event) => {
      if (!isCurrent()) return;
      try {
        const data = JSON.parse(event.data);

        // Any inbound frame proves the socket is alive, so the watchdog is fed
        // here rather than only on a probe reply. This is deliberately before
        // the type checks below: an unrecognised message still counts as
        // liveness.
        this.noteInboundTraffic();

        // ApplicationStatus is the reply to our own probe, and
        // ApplicationStatusRequest is a server-initiated heartbeat if
        // c1-services ever gains one. Neither is an application event, so both
        // stop here rather than reaching eventCentral.
        //
        // This used to test for messageType 'ping', which is not a type in this
        // protocol — the C1 message types are ApplicationStatusRequest and
        // ApplicationStatus. So the branch never matched, handleBackendPing was
        // unreachable, and the watchdog it was supposed to arm never started.
        if (data.messageType === 'ApplicationStatus'
          || data.messageType === 'ApplicationStatusRequest'
          || data.messageType === 'ping' || data.type === 'ping') {
          this.handleBackendPing(data);
          return;
        }

        // The probe is a protected frame — c1-services authorises it like any
        // other — so probing before the session exists would be refused. Start
        // once the server has confirmed auth.
        if (data.messageType === 'AuthConfirmation') {
          this.startLivenessProbe();
        }

        this.emitToHandlers(data.messageType || data.type, data);
        eventCentral.processMessage(data);
      } catch (_error) {
        // malformed message
      }
    };
  }

  // ── Network & visibility listeners (attached once) ───────────────────

  private setupNetworkListeners(): void {
    if (typeof window === 'undefined' || this.networkListenersAttached) return;
    this.networkListenersAttached = true;

    this.boundOnOnline = () => {
      this.statusCallbacks.onNetworkOnline?.();
      this.notifyHealthListeners();
      if (!this.isConnected() && !this.isManuallyDisconnected) {
        // Cancel any pending backoff timeout so we don't double-connect
        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
        }
        this.isReconnecting = false;
        this.reconnectAttempt = 0;
        this.connect(this.statusCallbacks);
      }
    };

    this.boundOnOffline = () => {
      this.statusCallbacks.onNetworkOffline?.();
      // Close the socket immediately so reconnect attempts start right away
      // rather than waiting for the heartbeat stale timer (35s)
      if (!this.isManuallyDisconnected && this.socket) {
        this.stopHeartbeat();
        this.setState('reconnecting');
        try { this.socket.close(); } catch (_) { }
        // onclose will fire and call attemptReconnection()
      }
      this.notifyHealthListeners();
    };

    window.addEventListener('online', this.boundOnOnline);
    window.addEventListener('offline', this.boundOnOffline);
  }

  private setupVisibilityListener(): void {
    if (typeof document === 'undefined' || this.visibilityListenerAttached) return;
    this.visibilityListenerAttached = true;

    this.boundOnVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible — check if socket is still alive
        if (this.socket?.readyState !== WebSocket.OPEN && !this.isManuallyDisconnected) {
          // Cancel any pending backoff timeout so we don't double-connect
          if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
          }
          this.isReconnecting = false;
          this.reconnectAttempt = 0;
          this.connect(this.statusCallbacks);
        } else if (this.isConnected()) {
          // Socket looks open but may be stale — check when we last heard from backend
          this.checkStale();
        }
      }
    };

    document.addEventListener('visibilitychange', this.boundOnVisibility);
  }

  // ── navigator.onLine poll ────────────────────────────────────────────

  private startOnlinePoll(): void {
    this.stopOnlinePoll();
    this.onlinePollTimer = setInterval(() => {
      if (!this.isNetworkOnline() && !this.isManuallyDisconnected) {
        this.stopOnlinePoll();
        this.stopHeartbeat();
        this.setState('reconnecting');
        if (this.socket) {
          try { this.socket.close(); } catch (_) { }
        }
        this.notifyHealthListeners();
      }
    }, 2000);
  }

  private stopOnlinePoll(): void {
    if (this.onlinePollTimer) {
      clearInterval(this.onlinePollTimer);
      this.onlinePollTimer = null;
    }
  }

  // ── Heartbeat (backend pings, UI pongs) ──────────────────────────────

  /**
   * Start monitoring. The stale-connection watchdog only arms itself after
   * the first ping is received from the backend — if the backend never pings,
   * no forced disconnects occur and we rely on the native onclose event.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    // The stale timer is still not armed here. It arms with the probe, once the
    // server confirms auth — a socket that is open but not yet authenticated has
    // no expectation of traffic, and arming now would time it out during login.
    this.startOnlinePoll();
  }

  private stopHeartbeat(): void {
    this.stopOnlinePoll();
    if (this.heartbeatStaleTimer) {
      clearTimeout(this.heartbeatStaleTimer);
      this.heartbeatStaleTimer = null;
    }
    if (this.heartbeatProbeTimer) {
      clearInterval(this.heartbeatProbeTimer);
      this.heartbeatProbeTimer = null;
    }
  }

  /**
   * Begin probing an authenticated connection, and arm the watchdog.
   *
   * Idempotent: AuthConfirmation can arrive again on a reconnect, and restarting
   * would otherwise stack intervals.
   */
  private startLivenessProbe(): void {
    if (this.heartbeatProbeTimer) {
      return;
    }

    this.sendLivenessProbe();
    this.heartbeatProbeTimer = setInterval(() => {
      this.sendLivenessProbe();
    }, HEARTBEAT_PROBE_INTERVAL);

    // Arm the watchdog now that traffic is expected.
    this.resetStaleTimer();
  }

  /**
   * Send one ApplicationStatusRequest.
   *
   * Goes through emit() so it carries a messageId — c1-services treats a frame
   * without one as a tripwire and drops the connection, so a probe built by hand
   * would cause the very disconnect this is meant to detect.
   *
   * The send time rides in the text because c1-services echoes it back
   * verbatim, which gives a true round trip rather than a one-way estimate.
   */
  private sendLivenessProbe(): void {
    if (!this.isConnected()) {
      return;
    }

    this.emit('ApplicationStatusRequest', {
      ApplicationStatusRequest: { text: `portal-liveness@${Date.now()}` },
    });
  }

  /** Record inbound traffic and push the stale deadline out, if armed. */
  private noteInboundTraffic(): void {
    this._lastPingReceivedAt = Date.now();
    if (this.heartbeatStaleTimer) {
      this.resetStaleTimer();
    }
  }

  /** Reset the stale-connection timer (called every time we receive a ping). */
  private resetStaleTimer(): void {
    if (this.heartbeatStaleTimer) {
      clearTimeout(this.heartbeatStaleTimer);
    }
    this.heartbeatStaleTimer = setTimeout(() => {
      this.handleStaleConnection();
    }, HEARTBEAT_STALE_TIMEOUT);
  }

  /**
   * Handle a status frame: the reply to our probe, or a server-initiated
   * heartbeat if one ever exists.
   *
   * Latency comes from the send time we put in the probe text, which
   * c1-services echoes verbatim. The previous version read data.timestamp,
   * which this protocol does not carry — messages use messageTimestamp, and the
   * status payload is nested under its type name — so latency stayed null even
   * on the path where a ping did arrive.
   *
   * The portal does not reply. Nothing on the server is waiting for one.
   */
  private handleBackendPing(data: any): void {
    const now = Date.now();
    this._lastPingReceivedAt = now;

    const echoed = data.ApplicationStatus?.text ?? data.ApplicationStatusRequest?.text;
    const sentAt = typeof echoed === 'string' && echoed.startsWith('portal-liveness@')
      ? Number(echoed.slice('portal-liveness@'.length))
      : NaN;

    if (Number.isFinite(sentAt)) {
      this._latencyMs = now - sentAt;
    } else if (typeof data.timestamp === 'number') {
      // Server-initiated heartbeat carrying its own timestamp: one-way estimate.
      this._latencyMs = now - data.timestamp;
    }

    // Debounce the "slow connection" signal across probe replies rather than
    // reacting to one sample — see SLOW_LATENCY_CONSECUTIVE_SAMPLES.
    if (this._latencyMs != null && this._latencyMs > SLOW_LATENCY_THRESHOLD_MS) {
      this._consecutiveSlowSamples++;
    } else {
      this._consecutiveSlowSamples = 0;
    }

    this.resetStaleTimer();
    this.notifyHealthListeners();
  }

  /**
   * Check on tab focus, where timers do not fire reliably.
   *
   * A backgrounded or sleeping tab is the case the interval cannot cover, and it
   * is also the most likely way to end up holding a socket the network dropped
   * without a close frame. Only meaningful once traffic is expected, hence the
   * guard on the probe being armed.
   */
  private checkStale(): void {
    if (this.heartbeatProbeTimer && this._lastPingReceivedAt) {
      const elapsed = Date.now() - this._lastPingReceivedAt;
      if (elapsed > HEARTBEAT_STALE_TIMEOUT) {
        this.handleStaleConnection();
      }
    }
  }

  private handleStaleConnection(): void {
    // Nothing inbound for HEARTBEAT_STALE_TIMEOUT despite our probes — the
    // socket is dead, or dead enough. Close it and let the existing reconnect
    // path take over.
    this._latencyMs = null;
    this.stopHeartbeat();
    // Update state immediately so the indicator goes amber before onclose fires
    if (!this.isManuallyDisconnected) {
      this.setState('reconnecting');
    }
    if (this.socket) {
      try { this.socket.close(); } catch (_) { /* ignore */ }
    }
  }

  // ── Reconnection ────────────────────────────────────────────────────

  private attemptReconnection(): void {
    if (this.isReconnecting || this.isManuallyDisconnected) {
      return;
    }

    if (this.reconnectAttempt >= this.maxReconnectAttempts) {
      this.setState('failed');
      this.statusCallbacks.onLogout?.();
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempt++;
    this.setState('reconnecting');

    // Calculate delay with exponential backoff and jitter
    const baseDelay = this.config.reconnectionDelay;
    const maxDelay = this.config.reconnectionDelayMax;
    const exponentialDelay = Math.min(maxDelay, baseDelay * Math.pow(2, this.reconnectAttempt - 1));
    const jitter = exponentialDelay * this.config.randomizationFactor * Math.random();
    const delay = exponentialDelay + jitter;

    this.statusCallbacks.onReconnecting?.(this.reconnectAttempt);

    // Start countdown for UI
    this.startReconnectCountdown(delay);

    this.reconnectTimeout = setTimeout(() => {
      this.isReconnecting = false;
      this._reconnectInSeconds = null;
      this.clearReconnectCountdown();
      this.connect(this.statusCallbacks);
    }, delay);
  }

  private startReconnectCountdown(delayMs: number): void {
    this._reconnectInSeconds = Math.ceil(delayMs / 1000);
    this.notifyHealthListeners();

    this.clearReconnectCountdown();
    this._reconnectCountdownTimer = setInterval(() => {
      if (this._reconnectInSeconds != null && this._reconnectInSeconds > 0) {
        this._reconnectInSeconds--;
        this.notifyHealthListeners();
      } else {
        this.clearReconnectCountdown();
      }
    }, 1000);
  }

  private clearReconnectCountdown(): void {
    if (this._reconnectCountdownTimer) {
      clearInterval(this._reconnectCountdownTimer);
      this._reconnectCountdownTimer = null;
    }
  }

  // ── Message queue ────────────────────────────────────────────────────

  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) return;

    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.send(message);
    }
  }

  private emitToHandlers(event: string, data: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  // Public method to emit to handlers (used by eventCentral to forward responses)
  public forwardToHandlers(event: string, data: any): void {
    this.emitToHandlers(event, data);
  }

  // Public API methods

  getSocket(): WebSocket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  isNetworkOnline(): boolean {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  }

  emit(event: string, data?: any, callback?: Function): void {
    const message = {
      messageType: event,
      messageId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      messageTimestamp: new Date().toISOString(),
      ...data
    };

    this.send(message);

    if (callback) {
      // Set up one-time handler for response
      const responseHandler = (response: any) => {
        callback(response);
        this.off(event + ':response', responseHandler);
      };
      this.on(event + ':response', responseHandler);
    }
  }

  on(event: string, callback: Function): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(callback);
  }

  once(event: string, callback: Function): void {
    const wrappedCallback = (data: any) => {
      callback(data);
      this.off(event, wrappedCallback);
    };
    this.on(event, wrappedCallback);
  }

  off(event: string, callback?: Function): void {
    if (!callback) {
      this.eventHandlers.delete(event);
      return;
    }

    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(callback);
      if (handlers.size === 0) {
        this.eventHandlers.delete(event);
      }
    }
  }

  send(data: any): void {
    if (!this.socket) {
      return;
    }

    if (this.socket.readyState === WebSocket.OPEN) {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.socket.send(message);
    } else {
      this.messageQueue.push(data);
    }
  }

  disconnect(): void {
    this.isManuallyDisconnected = true;
    this.stopHeartbeat();
    this.clearReconnectCountdown();
    eventCentral.clearSegmentBuffers();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this._latencyMs = null;
    this._lastPingReceivedAt = null;
    this._connectedSince = null;
    this._reconnectInSeconds = null;
    this.setState('disconnected');
  }

  onStatusChange(callbacks: SocketStatusCallbacks): void {
    this.statusCallbacks = { ...this.statusCallbacks, ...callbacks };
  }

  async emitWithAck(event: string, data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Request timeout'));
      }, 10000);

      this.emit(event, data, (response: any) => {
        clearTimeout(timeout);
        resolve(response);
      });
    });
  }

  setupMessageListener(): void {
    this.on('message', (data: any) => {
      eventCentral.processMessage(data);
    });

    this.on('notification', (data: any) => {
      eventCentral.processMessage(data);
    });

  }

  reconnect(): void {
    this.stopHeartbeat();
    this.clearReconnectCountdown();
    if (this.socket) {
      this.closeExistingSocket();
      this.socket = null;
    }
    this.reconnectAttempt = 0;
    this.isManuallyDisconnected = false;
    this._latencyMs = null;
    this._reconnectInSeconds = null;
    this.connect(this.statusCallbacks);
  }

  /** Notify provider that auth is complete so health reflects 'authenticated'. */
  markAuthenticated(): void {
    this.reconnectAttempt = 0;
    this._consecutiveSlowSamples = 0;
    this.setState('authenticated');
    this.flushMessageQueue();
  }

  /** Notify provider that auth is in progress. */
  markAuthenticating(): void {
    this.setState('authenticating');
  }
}

const socketServiceInstance = new SocketService();

export default socketServiceInstance;
export const socketService = socketServiceInstance;
